import { defineStore } from 'pinia';
import { notificationRepository } from '@/service/repository/notificationRepository';
import store from '@/store';
import { filterPendingByCopilotSelectedRole } from '@/utils';

/** Staff upselling KPI report — safe to cache; not the pending notification queue. */
const UPSELLING_REPORT_TTL_MS = 10 * 60 * 1000;

function isUpsellingReportEnabled() {
	const v = store.getters['venue/copilotConfig']?.upsellingDrinksReminder;
	return v != null && v !== 0;
}

export const useNotificationStore = defineStore('notification', {
	state: () => ({
		notificationsType: {
			SECOND_DRINK: 'upselling_drinks_reminder',
			KIOSK_WALK_IN: 'kiosk_walk_in',
			KIOSK_QR_SCAN: 'kiosk_qr_scan',
			CONSUMER_READY_TO_ORDER: 'customer_ready_to_order',
			ACCOUNT_SEATED: 'account_seated',
			TABLE_TAKE_ORDER_REMINDER: 'table_take_order_reminder',
			TABLE_CLEAN_REMINDER: 'table_clean_reminder',
		},

		notificationUpsellingActions: {
			NOTHING: 'nothing',
			CART: 'cart',
		},
		// Recent muestra los últimos 10min
		// Main notifications array containing the unified response
		notifications: { history: [], pending: [] }, // Contains { history: [...], pending: [...] }

		isLoadingNotifications: false,
		isLoadingTicketNotifications: false,
		lastUpdateTime: null,
		upsellingNotificationsData: [],
		lastUpsellingFetchAt: null,
		isLoadingUpsellingReport: false,
	}),

	getters: {
		// Main getter
		getNotifications: (state) => state.notifications,

		// Get history and pending from the unified response
		getHistoryNotifications: (state) => filterPendingByCopilotSelectedRole(state.notifications?.history || []),
		getPendingNotifications: (state) => filterPendingByCopilotSelectedRole(state.notifications?.pending || []),

		// Get pending notifications filtered by type
		getPendingTicketNotifications: (state) => {
			const list = state.notifications?.pending?.filter((notification) => notification.type === 'ticket_request') || [];
			return filterPendingByCopilotSelectedRole(list);
		},
		getPendingUpsellingNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(state, (n) => n.type === 'upselling', 'normalizedRoomsOnly'),
			),

		getAllPendingAccountKioskNotification: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(
					state,
					(n) => (n.type === 'kiosk_walk_in' || n.type === 'kiosk_qr_scan') && n.status === 0,
					'roomsOrReservationRoomName',
				),
			),

		getAllCustomerReadyToOrderNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingBySelectedZonesAndRangeTableNames(
					state,
					(n) => n.status === 0 && n.type === state.notificationsType.CONSUMER_READY_TO_ORDER,
					'roomsOrReservationRoomName',
				),
			),

		getAllAccountSeatedNotifications: (state) => {
			const seated = filterPendingByTypeAndCopilotZoneRange(state, state.notificationsType.ACCOUNT_SEATED);
			const takeOrder = filterPendingByTypeAndCopilotZoneRange(
				state,
				state.notificationsType.TABLE_TAKE_ORDER_REMINDER,
			);
			const takeOrderWids = new Set(takeOrder.map((n) => notificationReservationWid(n)).filter((w) => w != null));
			const merged = seated.filter((n) => {
				const wid = notificationReservationWid(n);
				return wid == null || !takeOrderWids.has(wid);
			});
			return filterPendingByCopilotSelectedRole(merged);
		},

		getAllTableTakeOrderReminderNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingByTypeAndCopilotZoneRange(state, state.notificationsType.TABLE_TAKE_ORDER_REMINDER),
			),

		/** Misma lógica de filtro por zona/rango que recordatorio ir a comandar. */
		getAllTableCleanReminderNotifications: (state) =>
			filterPendingByCopilotSelectedRole(
				filterPendingByTypeAndCopilotZoneRange(state, state.notificationsType.TABLE_CLEAN_REMINDER),
			),

		getNotificationsForType: (state, typeArray) => {
			const zoneFilterActive = store.getters['copilot/zoneFilter'];

			const notifications =
				state.notifications?.pending?.filter((notification) => notification.type === typeArray) || [];

			let zoneFiltered = notifications;
			if (zoneFilterActive) {
				const selectedZones = store.getters['copilot/selectedZoneFilter'] || [];
				if (selectedZones.length > 0) {
					zoneFiltered = notifications.filter((notification) => {
						if (notification.rooms && Array.isArray(notification.rooms)) {
							return notification.rooms.some((room) => selectedZones.includes(room.name));
						}
						return false;
					});
				}
			}

			return filterPendingByCopilotSelectedRole(zoneFiltered);
		},

		// Legacy getter for backward compatibility
		getTicketNotifications: (state) => {
			const list = state.notifications?.pending?.filter((notification) => notification.type === 'ticket_request') || [];
			return filterPendingByCopilotSelectedRole(list);
		},

		getAllNotificationUpsellingDrinksReminder: (state) => {
			const notifications =
				state.notifications?.pending?.filter((n) => n.type === 'upselling_drinks_reminder' && n.status === 0) || [];

			const selectedZones = store.getters['copilot/selectedZoneFilter'] || [];
			const selectedRangeIds = store.getters['copilot/selectedRangeFilter'] || [];

			if (selectedZones.length === 0 && selectedRangeIds.length === 0) {
				return filterPendingByCopilotSelectedRole(notifications);
			}

			const normalizedZones = selectedZones.map((z) => String(z).toLowerCase().trim());
			const allowedTables = new Set();
			const serviceRanges = store.getters['copilot/serviceRanges'] || [];

			if (selectedRangeIds.length > 0) {
				for (const id of selectedRangeIds.map(String)) {
					const range = serviceRanges.find((r) => String(r.id) === id);
					if (!range) continue;

					if (Array.isArray(range.tables)) {
						range.tables.forEach((t) => allowedTables.add(String(t?.name ?? t).trim()));
					}

					if (range.from != null && range.to != null) {
						const a = Number(range.from);
						const b = Number(range.to);
						if (!Number.isNaN(a) && !Number.isNaN(b)) {
							const min = Math.min(a, b);
							const max = Math.max(a, b);
							for (let i = min; i <= max; i++) allowedTables.add(String(i));
						}
					}
				}
			}

			const zoneFiltered = notifications.filter((n) => {
				// 1) Zone Match
				const matchZone =
					Array.isArray(n.rooms) &&
					n.rooms.some((r) => {
						const name = r.name?.toLowerCase().trim();
						return normalizedZones.includes(name);
					});

				// 2) Range Match
				let matchRange = false;
				if (allowedTables.size > 0) {
					const candidates = new Set();
					if (n.tableNumber != null) candidates.add(String(n.tableNumber).trim());
					if (Array.isArray(n.rooms)) {
						n.rooms.forEach((room) => {
							if (Array.isArray(room.tables)) {
								room.tables.forEach((t) => candidates.add(String(t?.name ?? t).trim()));
							}
						});
					}
					for (const t of candidates) {
						if (allowedTables.has(t)) {
							matchRange = true;
							break;
						}
					}
				}

				return matchZone || matchRange;
			});
			return filterPendingByCopilotSelectedRole(zoneFiltered);
		},

		getAllReservationsNotificationUpsellingDrinksReminder: (state) => {
			const notifications =
				state.notifications?.pending?.filter(
					(notification) => notification.type === 'upselling_drinks_reminder' && notification.status === 0,
				) || [];

			// Get selected zones from Vuex store
			const selectedZones = store.getters['copilot/selectedZoneFilter'] || [];

			// Filter notifications by selected zones
			let filteredNotifications = notifications;
			if (selectedZones.length > 0) {
				filteredNotifications = notifications.filter((notification) => {
					// Check if notification has rooms and if any room name matches selected zones
					if (notification.rooms && Array.isArray(notification.rooms)) {
						return notification.rooms.some((room) => selectedZones.includes(room.name));
					}
					return false; // If no rooms, exclude the notification
				});
			}

			filteredNotifications = filterPendingByCopilotSelectedRole(filteredNotifications);

			// Get copilot reservations from Vuex store
			const copilotReservations = store.getters['copilot/reservations'] || [];

			return filteredNotifications
				.map((notification) => {
					const reservationWid = notification.reservations?.[0]?.wid;
					const reservation = copilotReservations.find((r) => r.id === reservationWid);

					return {
						...reservation,
						notificationId: notification.id,
					};
				})
				.filter((r) => r != null);
		},

		// Get drinks for a specific reservation (migrated from Vuex)
		getAllDrinksForReservation: () => (reservation) => {
			if (reservation && reservation.cartSession) {
				const productOrders = reservation.cartSession?.productOrders || [];
				return productOrders
					.flatMap((order) => order.items || [])
					.filter((item) => {
						if (item.type) {
							return item.type.toLowerCase() === 'bebida';
						}
					});
			}

			return [];
		},

		// Loading states
		getIsLoadingNotifications: (state) => state.isLoadingNotifications,
		getIsLoadingTicketNotifications: (state) => state.isLoadingTicketNotifications,
		getLastUpdateTime: (state) => state.lastUpdateTime,

		// Zone filtering
		getSelectedZoneFilter: () => {
			return store.getters['copilot/selectedZoneFilter'] || [];
		},
	},

	actions: {
		// Main action for the unified notifications endpoint
		async loadNotifications(date = null) {
			try {
				this.isLoadingNotifications = true;
				this.notifications = await notificationRepository.getNotifications(date);
				this.lastUpdateTime = new Date();
			} catch (error) {
				console.error('Error loading notifications:', error);
			} finally {
				this.isLoadingNotifications = false;
			}
		},

		/** Inserta al instante notificaciones Copilot recibidas por WebSocket (mismo payload que el audio). */
		upsertPendingAccountSeatedFromPush(notification) {
			const types = ['account_seated', 'table_take_order_reminder', 'table_clean_reminder'];
			if (!notification?.id || !types.includes(notification.type)) {
				return;
			}
			if (!this.notifications.pending) {
				this.notifications.pending = [];
			}
			const widIncoming = notification.reservations?.[0]?.wid;
			if (notification.type === 'table_take_order_reminder' && widIncoming != null) {
				const widStr = String(widIncoming);
				this.notifications.pending = this.notifications.pending.filter((n) => {
					if (n.type !== this.notificationsType.ACCOUNT_SEATED) return true;
					const w = n.reservations?.[0]?.wid;
					return w == null || String(w) !== widStr;
				});
			}
			if (this.notifications.pending.some((n) => n.id === notification.id)) {
				return;
			}
			const row = {
				...notification,
				status: notification.status !== undefined ? notification.status : 0,
			};
			this.notifications.pending = [row, ...this.notifications.pending];
		},

		// Legacy actions for backward compatibility (kept for existing code)
		async loadRecentNotifications() {
			try {
				this.isLoadingNotifications = true;
				const notifications = await notificationRepository.getRecentNotifications();
				this.notifications = { history: notifications, pending: [] };
				this.lastUpdateTime = new Date();
			} catch (error) {
				console.error('Error loading recent notifications:', error);
			} finally {
				this.isLoadingNotifications = false;
			}
		},

		async loadTicketRequestNotifications() {
			try {
				this.isLoadingTicketNotifications = true;
				const ticketNotifications = await notificationRepository.getTicketRequestNotifications();
				this.notifications = { history: [], pending: ticketNotifications };
				this.lastUpdateTime = new Date();
			} catch (error) {
				console.error('Error loading ticket request notifications:', error);
			} finally {
				this.isLoadingTicketNotifications = false;
			}
		},

		async loadAllNotifications() {
			// Use the new unified endpoint instead of separate calls
			await this.loadNotifications();
		},

		// Add a new notification (for real-time updates)
		addNotification(notification) {
			// Add to history notifications
			if (!this.notifications.history) {
				this.notifications.history = [];
			}
			this.notifications.history.unshift(notification);
		},

		// Update notification status (e.g., when acknowledged)
		updateNotification({ id, updates }) {
			// Update in history notifications
			if (this.notifications.history) {
				const index = this.notifications.history.findIndex((n) => n.id === id);
				if (index !== -1) {
					this.notifications.history[index] = { ...this.notifications.history[index], ...updates };
				}
			}
			// Update in pending notifications
			if (this.notifications.pending) {
				const index = this.notifications.pending.findIndex((n) => n.id === id);
				if (index !== -1) {
					this.notifications.pending[index] = { ...this.notifications.pending[index], ...updates };
				}
			}
		},

		// Remove notification (e.g., when dismissed)
		removeNotification(id) {
			// Remove from history notifications
			if (this.notifications.history) {
				this.notifications.history = this.notifications.history.filter((n) => n.id !== id);
			}
			// Remove from pending notifications
			if (this.notifications.pending) {
				this.notifications.pending = this.notifications.pending.filter((n) => n.id !== id);
			}
		},

		// Clear all notifications
		clearNotifications() {
			this.notifications = { history: [], pending: [] };
		},

		// Legacy action for backward compatibility
		async acknowledgeNotifications(notificationId, notificationData) {
			try {
				const response = await notificationRepository.acknowledgeNotification(notificationId, notificationData);

				if (response.ok) {
					// Remove the acknowledged notification
					this.removeNotification(notificationId);
					return response;
				} else {
					return response;
				}
			} catch (error) {
				console.error('Error acknowledging notification:', error);
				throw error;
			}
		},

		async loadUpsellingReportIfEnabled({ force = false } = {}) {
			if (!isUpsellingReportEnabled()) {
				return;
			}
			await this.loadNotificationsUpsellingData({ force });
		},

		async loadNotificationsUpsellingData({ force = false } = {}) {
			if (!force && this.lastUpsellingFetchAt != null) {
				const elapsed = Date.now() - this.lastUpsellingFetchAt;
				if (elapsed < UPSELLING_REPORT_TTL_MS) {
					return;
				}
			}
			if (this.isLoadingUpsellingReport) {
				return;
			}

			try {
				this.isLoadingUpsellingReport = true;
				const logicalDate = store.getters['copilot/selectedDate'];
				const response = await notificationRepository.getUpsellingNotificationsData(logicalDate, logicalDate);
				const next = response !== undefined && response !== null ? response : [];
				this.$patch({
					upsellingNotificationsData: next,
					lastUpsellingFetchAt: Date.now(),
				});
			} catch (error) {
				console.error('Error loading upselling notifications report', error);
				this.$patch({ upsellingNotificationsData: [] });
			} finally {
				this.isLoadingUpsellingReport = false;
			}
		},
	},
});

/**
 * @param {object} notification
 * @returns {string|null}
 */
function notificationReservationWid(notification) {
	const w = notification?.reservations?.[0]?.wid;
	return w == null ? null : String(w);
}

/**
 * Vuex `copilot/selectedRangeTableNames` devuelve un array ordenado; en otros sitios podría ser Set.
 * @param {Set<string>|string[]|undefined|null} value
 * @returns {Set<string>|null} null si no hay selección útil
 */
function toRangeTableNameSet(value) {
	if (value == null) return null;
	if (value instanceof Set) {
		return value.size > 0 ? value : null;
	}
	if (Array.isArray(value)) {
		return value.length > 0 ? new Set(value.map((n) => String(n))) : null;
	}
	return null;
}

/**
 * Coincide con selectedRangeTableNames del Copilot (reservas / mesas en el payload).
 * @param {object} notification
 * @param {Set<string>|null} rangeTableNames
 */
function notificationMatchesSelectedRangeTableNames(notification, rangeTableNames) {
	if (!rangeTableNames || rangeTableNames.size === 0 || !notification.reservations) {
		return false;
	}
	return notification.reservations.some((res) => {
		return res.tables?.some((t) => rangeTableNames.has(String(t.name))) || rangeTableNames.has(String(res.table));
	});
}

/**
 * Pending filtradas por predicado, luego por zona + selectedRangeTableNames (Vuex).
 * @param {{ notifications?: { pending?: unknown[] } }} state
 * @param {(n: object) => boolean} predicate
 * @param {'normalizedRoomsOnly' | 'roomsOrReservationRoomName'} zoneMode
 *   - normalizedRoomsOnly: solo rooms, nombres en minúsculas (upselling)
 *   - roomsOrReservationRoomName: rooms o res.roomName con includes exacto (kiosk, listo para pedir)
 */
function filterPendingBySelectedZonesAndRangeTableNames(state, predicate, zoneMode) {
	const notifications = (state.notifications?.pending || []).filter(predicate);

	const selectedZones = store.getters['copilot/selectedZoneFilter'] || [];
	const rangeTableNames = toRangeTableNameSet(store.getters['copilot/selectedRangeTableNames']);

	if (selectedZones.length === 0 && !rangeTableNames) {
		return notifications;
	}

	if (zoneMode === 'normalizedRoomsOnly') {
		const normalizedZones = selectedZones.map((z) => String(z).toLowerCase().trim());
		return notifications.filter((notification) => {
			const matchZone =
				notification.rooms?.some((room) => {
					const name = room.name?.toLowerCase().trim();
					return normalizedZones.includes(name);
				}) ?? false;
			const matchRange = notificationMatchesSelectedRangeTableNames(notification, rangeTableNames);
			return matchZone || matchRange;
		});
	}

	return notifications.filter((notification) => {
		const matchZone =
			(notification.rooms?.some((room) => selectedZones.includes(room.name)) ?? false) ||
			(notification.reservations?.some((res) => selectedZones.includes(res.roomName)) ?? false);
		const matchRange = notificationMatchesSelectedRangeTableNames(notification, rangeTableNames);
		return matchZone || matchRange;
	});
}

/**
 * Pending con status 0 y tipo dado, filtradas por zona y rangos de servicio del Copilot (Vuex).
 * @param {{ notifications?: { pending?: unknown[] } }} state
 * @param {string} notificationType
 */
function filterPendingByTypeAndCopilotZoneRange(state, notificationType) {
	const pending = state.notifications?.pending || [];
	const notifications = pending.filter(
		(notification) => notification.status === 0 && notification.type === notificationType,
	);

	const selectedZones = store.getters['copilot/selectedZoneFilter'] || [];
	const selectedRangeIds = store.getters['copilot/selectedRangeFilter'] || [];

	if (selectedZones.length === 0 && selectedRangeIds.length === 0) {
		return notifications;
	}

	const normalizedZones = selectedZones.map((zone) => String(zone).toLowerCase().trim());
	const allowedTables = new Set();
	const serviceRanges = store.getters['copilot/serviceRanges'] || [];

	if (selectedRangeIds.length > 0) {
		for (const rangeId of selectedRangeIds.map(String)) {
			const rangeIdString = String(rangeId);
			const range = serviceRanges.find((r) => String(r.id) === rangeIdString);
			if (!range) continue;

			if (Array.isArray(range.tables)) {
				range.tables.forEach((table) => allowedTables.add(String(table?.name ?? table).trim()));
			}

			if (range.from != null && range.to != null) {
				const a = Number(range.from);
				const b = Number(range.to);
				if (!Number.isNaN(a) && !Number.isNaN(b)) {
					const min = Math.min(a, b);
					const max = Math.max(a, b);
					for (let i = min; i <= max; i++) allowedTables.add(String(i));
				}
			}
		}
	}

	const copilotReservations = store.getters['copilot/reservations'] || [];

	return notifications.filter((n) => {
		const matchZone =
			Array.isArray(n.rooms) &&
			n.rooms.some((r) => {
				const name = r.name?.toLowerCase().trim();
				return normalizedZones.includes(name);
			});

		let matchRange = false;
		if (allowedTables.size > 0) {
			const candidates = new Set();
			if (n.tableNumber != null) candidates.add(String(n.tableNumber).trim());
			if (Array.isArray(n.rooms)) {
				n.rooms.forEach((room) => {
					if (Array.isArray(room.tables)) {
						room.tables.forEach((t) => candidates.add(String(t?.name ?? t).trim()));
					}
				});
			}
			const wid = n.reservations?.[0]?.wid;
			if (wid != null) {
				const res = copilotReservations.find((r) => r.id === wid);
				if (res?.table != null) {
					if (Array.isArray(res.table)) {
						res.table.forEach((x) => candidates.add(String(x?.name ?? x).trim()));
					} else {
						candidates.add(String(res.table).trim());
					}
				}
			}
			for (const t of candidates) {
				if (allowedTables.has(t)) {
					matchRange = true;
					break;
				}
			}
		}

		return matchZone || matchRange;
	});
}
